import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import React from "react"
import { NavLink, Navigate, Outlet, useLocation, useNavigate } from "react-router-dom"
import {
  listPortalNotifications,
  markPortalNotificationRead,
  type PortalNotificationRow,
} from "../api/endpoints/portal"
import { useTranslation } from "react-i18next"
import { useAuth } from "../stores/auth"
import { formatDateTime } from "../utils/formatting"
import { getPortalNotificationPresentation } from "../utils/notifications"
import { InstallPrompt } from "./install_prompt"
import { UpdateBanner } from "./update_banner"

function useOnlineStatus() {
  const [online, setOnline] = React.useState(() => navigator.onLine)

  React.useEffect(() => {
    function handleOnline() {
      setOnline(true)
    }
    function handleOffline() {
      setOnline(false)
    }

    window.addEventListener("online", handleOnline)
    window.addEventListener("offline", handleOffline)
    return () => {
      window.removeEventListener("online", handleOnline)
      window.removeEventListener("offline", handleOffline)
    }
  }, [])

  return online
}

export function RequireAuth({ children }: { children: React.ReactNode }) {
  const { client, isLoading } = useAuth()

  if (isLoading) {
    return <div className="screen-shell">Loading...</div>
  }

  if (!client) {
    return <Navigate replace to="/login" />
  }

  return <>{children}</>
}

export function PortalLayout() {
  const { t } = useTranslation()
  const { client, logout } = useAuth()
  const location = useLocation()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const isOnline = useOnlineStatus()
  const [notificationsOpen, setNotificationsOpen] = React.useState(false)
  const notificationsQuery = useQuery({
    queryKey: ["portal-notifications", 1],
    queryFn: () => listPortalNotifications(1, 8).then((response) => response),
    refetchInterval: 15_000,
  })
  const markReadMutation = useMutation({
    mutationFn: (notificationId: string) => markPortalNotificationRead(notificationId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["portal-notifications"] })
    },
  })

  const notificationRows = notificationsQuery.data?.data ?? []
  const unreadCount = notificationsQuery.data?.meta?.unreadCount ?? 0

  React.useEffect(() => {
    setNotificationsOpen(false)
  }, [location.pathname])

  function openNotification(notification: PortalNotificationRow) {
    const presentation = getPortalNotificationPresentation(notification)

    if (!notification.isRead) {
      markReadMutation.mutate(notification.notificationId)
    }

    setNotificationsOpen(false)

    if (presentation.destinationPath.startsWith("/")) {
      navigate(presentation.destinationPath)
      return
    }

    window.location.assign(presentation.destinationPath)
  }

  return (
    <div className="portal-shell">
      <header className="portal-topbar">
        <div>
          <p className="eyebrow">{t("app.borrowerPortal")}</p>
          <h1>LoanCore</h1>
          <p className="subtle">{client?.fullName}</p>
        </div>
        <div className="button-row">
          <div className="notification-shell">
            <button
              className="ghost-button"
              onClick={() => setNotificationsOpen((value) => !value)}
              type="button"
            >
              {t("notifications.title")}
              {unreadCount > 0 ? <span className="notification-badge">{unreadCount}</span> : null}
            </button>
            {notificationsOpen ? (
              <div className="notification-panel">
                <div className="notification-panel-header">
                  <div>
                    <p className="eyebrow">{t("notifications.title")}</p>
                    <strong>
                      {unreadCount > 0
                        ? t("notifications.unreadSummary", { count: unreadCount })
                        : t("notifications.allCaughtUp")}
                    </strong>
                  </div>
                  <button
                    className="ghost-button notification-link-button"
                    onClick={() => {
                      setNotificationsOpen(false)
                      navigate("/notifications")
                    }}
                    type="button"
                  >
                    {t("notifications.viewAll")}
                  </button>
                </div>
                {notificationRows.length === 0 ? (
                  <p className="empty-copy">{t("notifications.empty")}</p>
                ) : (
                  notificationRows.map((notification) => {
                    const presentation = getPortalNotificationPresentation(notification)

                    return (
                      <button
                        className={`notification-row ${notification.isRead ? "" : "unread"}`}
                        key={notification.notificationId}
                        onClick={() => openNotification(notification)}
                        type="button"
                      >
                        <span className="notification-row-meta">
                          <span className={`status-pill ${presentation.tone}`}>
                            {presentation.eventLabel}
                          </span>
                          {!notification.isRead ? (
                            <span className="notification-status">{t("notifications.unreadLabel")}</span>
                          ) : null}
                        </span>
                        <strong>{notification.title}</strong>
                        <span className="notification-category">{presentation.categoryLabel}</span>
                        <span>{notification.body}</span>
                        <span className="subtle">{formatDateTime(notification.createdAt)}</span>
                      </button>
                    )
                  })
                )}
              </div>
            ) : null}
          </div>
          <button className="ghost-button" onClick={() => logout()} type="button">
            {t("app.logout")}
          </button>
        </div>
      </header>

      <UpdateBanner />

      {!isOnline ? (
        <div className="status-banner offline">
          {t("app.offline")}
        </div>
      ) : null}

      <main className="portal-main">
        <Outlet />
      </main>

      <InstallPrompt />

      <nav className="bottom-nav">
        <NavLink className={location.pathname === "/" ? "active" : ""} to="/">
          {t("nav.home")}
        </NavLink>
        <NavLink className={location.pathname.startsWith("/payments") ? "active" : ""} to="/payments">
          {t("nav.payments")}
        </NavLink>
        <NavLink className={location.pathname.startsWith("/support") ? "active" : ""} to="/support">
          {t("nav.support")}
        </NavLink>
        <NavLink className={location.pathname.startsWith("/profile") ? "active" : ""} to="/profile">
          {t("nav.profile")}
        </NavLink>
      </nav>
    </div>
  )
}
